<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <div class="row">
        <div class="col-md-12">
          <div class="form-group settings-search">
            <div class="input-group">
              <div class="input-group-addon"><i class="fa fa-fw fa-search"></i></div>
              <input type="search" class="form-control" ref="searchInput" v-model="searchQuery" :placeholder="_('Search settings')" v-on:keydown.enter.prevent="openFirstMatch" v-on:keydown.esc="clearSearch" />
            </div>
          </div>
        </div>
      </div>
      <p class="text-muted" v-if="isSearching && !filteredGroups.length">{{ _('No settings match the search.') }}</p>
      <template v-for="group in filteredGroups" :key="group.id">
        <div class="row">
          <div class="col-md-12">
            <h3><i :class="'fa fa-fw fa-' + group.icon"></i> {{ _(group.title) }}</h3>
          </div>
        </div>
        <div class="settings-row">
          <div class="settings-col" v-for="section in group.sections" :key="section.id">
            <div class="panel panel-success">
              <div class="panel-heading">
                <h4 class="panel-title">
                  <i :class="'fa fa-fw fa-' + section.icon"></i>
                  <a href="javascript:;" v-if="section.link" v-on:click="open(section.link.component, section.link.tab)"><search-highlight :text="sectionTitle(section)" :words="queryWords" /></a>
                  <span v-else><search-highlight :text="sectionTitle(section)" :words="queryWords" /></span>
                </h4>
              </div>
              <div class="panel-body">
                <div class="settings-link" v-for="item in section.items" :key="item.key">
                  <a href="javascript:;" v-on:click="open(item.component, item.tab)"><search-highlight :text="itemLabel(item)" :words="queryWords" /></a>
                  <div class="settings-search-hits" v-if="item.hits && item.hits.length">
                    <template v-for="(hit, index) in item.hits" :key="index">
                      <span v-if="index"> · </span>
                      <a href="javascript:;" v-on:click="open(item.component, item.tab, hit.focus)"><search-highlight :text="hit.text" :words="queryWords" /></a>
                    </template>
                    <span v-if="item.moreHits"> · +{{ item.moreHits }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
<style scoped>
.panel-title {
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.settings-search {
  margin-top: 20px;
  max-width: 480px;
}
.settings-search-hits {
  font-size: 0.9em;
  margin: 0 0 4px 12px;
}
.settings-search-hits a {
  color: inherit;
  opacity: 0.75;
}
/* Bootstrap pads <mark>, which splits a highlighted word ("Pay trail") */
.settings-row :deep(mark) {
  padding: 0;
  color: inherit;
}
</style>
<script>

import { h } from 'vue'
import settingshelpers from '~/lib/settingshelpers'
import settingssearch from '~/lib/settingssearch'
import components from '~/lib/components'
import componenttypes from '~/lib/componenttypes'
import messagetypes from '~/lib/messagetypes'
import { mapGetters } from 'vuex'
import { DateTime } from 'luxon'

const MAX_HITS = 8

// Lowercase and strip accents ("é" matches "e"), but keep å, ä and ö: in Finnish and Swedish they are
// letters of their own, so "sa" must not match "sä". Character by character, so that the length stays
// the same and the highlight positions stay valid.
const OWN_LETTERS = 'åäö'
function normalize (str) {
  return Array.from(String(str).normalize('NFC').toLowerCase(), c => OWN_LETTERS.includes(c) ? c : c.normalize('NFD').replace(/[\u0300-\u036f]/g, '')).join('')
}

function splitWords (str) {
  return normalize(str).split(/[^\p{L}\p{N}]+/u).filter(w => w.length)
}

// Translation keys can carry a context prefix ("storefront settings|Layout")
function stripContext (key) {
  const pos = key.indexOf('|')
  return pos >= 0 ? key.substring(pos + 1) : key
}

// Every query word must be found in the text, anywhere in a word, so that Finnish compound
// words such as "verkkomaksut" are found and typing more letters only narrows the results
function matchesAll (text, words) {
  if (!words.length) {
    return false
  }
  const textWords = splitWords(text)
  return words.every(w => textWords.some(tw => tw.includes(w)))
}

// Whether some query word starts a word of the text ("alv" in "ALV", not in "maksupalvelu")
function startsAnyWord (text, words) {
  const textWords = splitWords(text)
  return words.some(w => textWords.some(tw => tw.startsWith(w)))
}

// Renders the text with the matching query words wrapped in <mark>
const SearchHighlight = {
  props: ['text', 'words'],
  setup (props) {
    return () => {
      const text = props.text || ''
      const words = props.words || []
      if (!words.length) {
        return text
      }
      // Find the hits on the normalized text; normalization keeps the string length for
      // precomposed characters, so the positions are valid in the original text too
      const normalized = text.normalize('NFC')
      const haystack = normalize(normalized)
      if (haystack.length !== normalized.length) {
        return text
      }
      const marked = new Array(normalized.length).fill(false)
      for (const w of words) {
        let pos = haystack.indexOf(w)
        while (pos >= 0) {
          for (let i = pos; i < pos + w.length; i++) {
            marked[i] = true
          }
          pos = haystack.indexOf(w, pos + 1)
        }
      }
      const parts = []
      let start = 0
      for (let i = 1; i <= normalized.length; i++) {
        if (i === normalized.length || marked[i] !== marked[start]) {
          const part = normalized.substring(start, i)
          parts.push(marked[start] ? h('mark', part) : part)
          start = i
        }
      }
      return parts
    }
  }
}

export default {
  mixins: [components, settingshelpers, componenttypes, messagetypes],
  components: { SearchHighlight },
  data () {
    return {
      searchQuery: ''
    }
  },
  computed: {
    ...mapGetters(['userData']),
    canAccessAuditLog () {
      return this.userData && this.userData.userlevel >= 10
    },
    canAccessMax () {
      return this.isFeatureEnabled('max')
    },
    canAccessAnonymization () {
      return this.userData && this.userData.userlevel >= 7
    },
    canAccessRedirects () {
      return this.userData && this.userData.userlevel >= 7
    },
    canAccessAdmin () {
      return this.userData && this.userData.userlevel >= 10
    },
    advancedCollections () {
      return this.$store.getters.advancedCollections
    },
    // The menu. A section or an item with visible: false is left out, also from the search.
    // The search keywords of each item come from lib/settingssearch.js by 'component:tab'.
    groups () {
      const delivery2 = this.isFeatureEnabled('delivery2.0')
      return [
        {
          id: 'settings',
          title: 'Settings',
          icon: 'cog',
          sections: [
            {
              id: 'company',
              title: 'Company profile',
              icon: 'building-o',
              link: { component: 'CompanyProfile', tab: 'general' },
              items: [
                { label: 'General', component: 'CompanyProfile', tab: 'general' },
                { label: 'Billing info', component: 'CompanyProfile', tab: 'billing' },
                { label: 'Details', component: 'CompanyProfile', tab: 'details' },
                { label: 'Social media', component: 'CompanyProfile', tab: 'some' },
                { label: 'Images', component: 'CompanyProfile', tab: 'images' },
                { label: 'Johku Merchant Account', component: 'CompanyProfile', tab: 'account' }
              ]
            },
            {
              id: 'general',
              title: 'General settings',
              icon: 'cog',
              link: { component: 'Settings', tab: 'paymentmethods' },
              items: [
                { label: 'Paying', component: 'Settings', tab: 'paymentmethods' },
                { label: 'Payment Plans', component: 'Settings', tab: 'paymentplans', visible: this.isFeatureEnabled('paymentplans') },
                { label: 'Vouchers', component: 'Settings', tab: 'vouchers' },
                { label: 'Delivery methods', component: 'Settings', tab: 'deliverymethods', visible: !delivery2 },
                { label: 'Languages', component: 'Settings', tab: 'languages' },
                { label: 'Tags', component: 'Settings', tab: 'tags' },
                { label: 'Management users', component: 'Settings', tab: 'users' },
                { label: 'Other', component: 'Settings', tab: 'other' }
              ]
            },
            {
              id: 'communication',
              title: 'Communication',
              icon: 'messages',
              link: { component: 'CommunicationSettings', tab: 'messages' },
              items: [
                { label: 'Terms and standard messages', component: 'CommunicationSettings', tab: 'messages' },
                { label: 'Emails', component: 'CommunicationSettings', tab: 'emails' },
                { label: 'Product-specific terms', component: 'CommunicationSettings', tab: 'terms' },
                { label: 'Forms', component: 'CommunicationSettings', tab: 'forms', visible: this.isFeatureEnabled('forms') },
                { label: 'Templates', component: 'CommunicationSettings', tab: 'templates' }
              ]
            },
            {
              id: 'delivery',
              title: 'Delivery methods',
              icon: 'truck',
              visible: delivery2,
              link: { component: 'DeliverySettings', tab: 'deliverymethods' },
              items: [
                { label: 'Delivery methods', component: 'DeliverySettings', tab: 'deliverymethods' },
                { label: 'Delivery method groups', component: 'DeliverySettings', tab: 'deliverymethodgroups' },
                { label: 'Delivery settings', component: 'DeliverySettings', tab: 'settings' }
              ]
            },
            {
              id: 'storefront',
              title: 'Storefront',
              icon: 'globe',
              link: { component: 'StorefrontSettings', tab: 'frontpage' },
              items: [
                { label: 'Front page', component: 'StorefrontSettings', tab: 'frontpage' },
                { label: 'Search', component: 'StorefrontSettings', tab: 'search' },
                { label: 'Menu structure', component: 'StorefrontSettings', tab: 'menu' },
                { label: 'Widgets', component: 'StorefrontSettings', tab: 'widgets' },
                { label: 'Metadata and tracking', component: 'StorefrontSettings', tab: 'metadata' },
                { label: 'storefront settings|Layout', component: 'StorefrontSettings', tab: 'layout' },
                { label: 'Theme', component: 'StorefrontSettings', tab: 'theme' },
                { label: 'Cookie management', component: 'StorefrontSettings', tab: 'cookiebot', visible: this.isFeatureEnabled('cookiebot') },
                { label: 'Redirects', component: 'StorefrontSettings', tab: 'redirects', visible: this.canAccessRedirects },
                { label: 'Other', component: 'StorefrontSettings', tab: 'settings' }
              ]
            },
            {
              id: 'collections',
              title: ['Storefront', 'Collections'],
              icon: 'layer-group',
              link: { component: 'CollectionSettings', tab: 'collections' },
              items: [
                { label: 'Collections', component: 'CollectionSettings', tab: 'collections' },
                { label: 'User groups', component: 'CollectionSettings', tab: 'groups', visible: this.advancedCollections },
                { label: 'Collection users', component: 'CollectionSettings', tab: 'users' },
                { label: 'Sent invites', component: 'CollectionSettings', tab: 'invites' }
              ]
            },
            {
              id: 'pos',
              title: 'Johku App',
              icon: 'shopping-basket',
              link: { component: 'PosSettings', tab: 'cashregisters' },
              items: [
                { label: 'Points of use', component: 'PosSettings', tab: 'pointsofuse' },
                { label: 'User interfaces', component: 'PosSettings', tab: 'userinterfaces' },
                { label: 'Layout sections', component: 'PosSettings', tab: 'layouts' },
                { label: 'Components', component: 'PosSettings', tab: 'components' },
                { label: 'Ready-made screens', component: 'UiFeatureCatalog', tab: 'general', visible: this.isFeatureEnabled('uifeatures') }
              ]
            },
            {
              id: 'accounting',
              title: 'Financial Management',
              icon: 'briefcase',
              link: { component: 'AccountingSettings', tab: 'settings' },
              items: [
                { label: (this.isBrioxSetupVisible || this.isNocfoSetupVisible) ? 'Netvisor' : 'Settings', component: 'AccountingSettings', tab: 'settings' },
                { label: 'Briox', component: 'AccountingSettings', tab: 'briox', visible: this.isBrioxSetupVisible },
                { label: 'NoCFO', component: 'AccountingSettings', tab: 'nocfo', visible: this.isNocfoSetupVisible },
                { label: 'Cost objects', component: 'AccountingSettings', tab: 'dimensions', visible: this.isAccountingConfigured },
                { label: 'Accounts', component: 'AccountingSettings', tab: 'accounts', visible: this.isAccountingConfigured },
                { label: 'Account mapping', component: 'AccountingSettings', tab: 'accountmapping', visible: this.isAccountingConfigured }
              ]
            },
            {
              id: 'saleschannels',
              title: 'Sales Channels',
              icon: 'chart-network',
              link: { component: 'SalesChannelSettings', tab: 'johkusaleschannels' },
              items: [
                { label: 'Johku Sales Channels', component: 'SalesChannelSettings', tab: 'johkusaleschannels' },
                { label: 'Other Sales Channels', component: 'SalesChannelSettings', tab: 'othersaleschannels' },
                { label: 'Shops Using Your Sales Channel', component: 'SalesChannelSettings', tab: 'shopsusingyoursaleschannel' },
                { label: 'My Sales Channel', component: 'SalesChannelSettings', tab: 'mysaleschannel' }
              ]
            },
            {
              id: 'smartdevices',
              title: 'Smart Devices',
              icon: 'house-signal',
              visible: this.isFeatureEnabled('smartdevices'),
              link: { component: 'SmartDeviceSettings', tab: 'settings' },
              items: [
                { label: 'Devices', component: 'SmartDeviceSettings', tab: 'devices' },
                { label: 'Linked Services', component: 'SmartDeviceSettings', tab: 'services' },
                { label: 'Actions', component: 'SmartDeviceSettings', tab: 'actions' }
              ]
            }
          ]
        },
        {
          id: 'service',
          title: 'Service management',
          icon: 'wrench',
          sections: [
            {
              id: 'logs',
              title: 'Logs',
              icon: 'file-text',
              items: [
                { label: 'Issues', component: 'IssueList', tab: 'issues' },
                { label: 'Notification schedule', component: 'NotificationSchedule', tab: 'general' },
                { label: 'Audit log', component: 'AuditLog', tab: 'general', visible: this.canAccessAuditLog }
              ]
            },
            {
              id: 'max',
              title: 'Johku Max',
              icon: 'wand-magic-sparkles',
              visible: this.canAccessMax,
              items: [
                { label: 'Audits and reports', component: 'MaxArtifactList', tab: 'general' },
                { label: 'Reports, dashboards and tools', component: 'MaxSpecList', tab: 'general' }
              ]
            },
            {
              id: 'privacy',
              title: 'Privacy',
              icon: 'user-secret',
              visible: this.canAccessAnonymization,
              items: [
                { label: 'Anonymization', component: 'AnonymizationSettings', tab: 'general' }
              ]
            },
            {
              id: 'admin',
              title: 'Administration',
              icon: 'wrench',
              visible: this.canAccessAdmin,
              items: [
                { label: 'Johku shops', component: 'Admin', tab: 'general' },
                { label: 'Campaigns', component: 'AdminCampaigns', tab: 'general' },
                { label: 'User access', component: 'AdminUserAccess', tab: 'general' }
              ]
            }
          ]
        }
      ]
    },
    // Keywords that come from the same lists the editors render, so they can't fall behind:
    // the email and message panels of CommunicationSettings (lib/messagetypes.js)
    generatedKeywords () {
      return {
        'CommunicationSettings:emails': this.messageTypes.map(m => ({ text: m.title, focus: '#' + m.type })),
        'CommunicationSettings:messages': this.additionalMessageTypes.map(m => ({ text: m.title, focus: '#' + m.type }))
      }
    },
    queryWords () {
      return splitWords(this.searchQuery)
    },
    isSearching () {
      return this.queryWords.length > 0
    },
    // The visible menu, narrowed down to the matching items when searching. An item matches
    // when its label, its section title and its keywords together contain every query word;
    // the keywords that match a query word on their own are listed under the item as hits.
    filteredGroups () {
      const words = this.queryWords
      const ret = []
      for (const group of this.groups) {
        const sections = []
        for (const section of group.sections) {
          if (section.visible === false) {
            continue
          }
          const items = []
          const titleTexts = this.searchTexts(section.title)
          const titleMatches = matchesAll(titleTexts.join(' '), words)
          for (const item of section.items) {
            if (item.visible === false) {
              continue
            }
            const key = item.component + ':' + item.tab
            if (!this.isSearching) {
              items.push({ ...item, key })
              continue
            }
            const keywords = this.itemKeywords(item)
            const haystack = [...titleTexts, ...this.searchTexts(item.label), ...keywords.map(k => k.texts.join(' '))].join(' ')
            if (!titleMatches && !matchesAll(haystack, words)) {
              continue
            }
            // Hits where a query word starts a word first
            const allHits = keywords
              .filter(k => k.texts.some(t => words.some(w => matchesAll(t, [w]))))
              .sort((a, b) => Number(b.texts.some(t => startsAnyWord(t, words))) - Number(a.texts.some(t => startsAnyWord(t, words))))
            const hits = allHits.slice(0, MAX_HITS).map(k => ({ text: k.display, focus: k.focus }))
            items.push({ ...item, key, hits, moreHits: allHits.length - hits.length })
          }
          if (items.length) {
            sections.push({ ...section, items })
          }
        }
        if (sections.length) {
          ret.push({ ...group, sections })
        }
      }
      return ret
    }
  },
  watch: {
    searchQuery (newValue) {
      this.persist('searchQuery', newValue, DateTime.now().plus({ hours: 2 }))
    }
  },
  mounted () {
    // No autofocus on touch screens, where it would pop up the keyboard
    if (window.matchMedia && !window.matchMedia('(pointer: coarse)').matches) {
      this.$refs.searchInput?.focus()
    }
  },
  methods: {
    // Both the translation and the English text, so that English terms work in every language
    searchTexts (keys) {
      const list = Array.isArray(keys) ? keys : [keys]
      const ret = []
      for (const key of list) {
        ret.push(this._(key), stripContext(key))
      }
      return ret
    },
    itemKeywords (item) {
      const key = item.component + ':' + item.tab
      const entries = [...(this.generatedKeywords[key] || [])]
      for (const entry of settingssearch[key] || []) {
        const text = typeof entry === 'string' ? entry : entry.text
        if (!entries.some(e => e.text === text)) {
          entries.push(entry)
        }
      }
      return entries.map(entry => {
        const text = typeof entry === 'string' ? entry : entry.text
        return {
          display: this._(text),
          texts: this.searchTexts(text),
          focus: typeof entry === 'string' ? null : entry.focus
        }
      })
    },
    sectionTitle (section) {
      return Array.isArray(section.title) ? section.title.map(t => this._(t)).join(': ') : this._(section.title)
    },
    itemLabel (item) {
      return this._(item.label)
    },
    clearSearch () {
      this.searchQuery = ''
    },
    openFirstMatch () {
      if (!this.isSearching) {
        return
      }
      const item = this.filteredGroups[0]?.sections[0]?.items[0]
      if (!item) {
        return
      }
      const hit = item.hits && item.hits.find(h => h.focus)
      this.open(item.component, item.tab, hit ? hit.focus : null)
    },
    open (section, tab, focus) {
      const initData = {
        activateTab: tab
      }
      if (focus) {
        initData.focus = focus
      }
      this.EventBus.$emit('showComponent', {
        shopId: this.shopId,
        component: section,
        title: this.getComponentName(section),
        initData
      })
    }
  }
}
</script>
